import { Title } from '@common/components';
import useFormatDate from '@common/hooks/useFormatDate';
import { formatDecimal } from '@common/util/math';
import { IdentificationStatsFactory } from '@dashboard/components/IdentificationStatsFactory';
import {
  getFormattedAge,
  getFormattedFraction,
  groupByCognitiveProcess,
} from '@dashboard/util/identification';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import classes from './StudentIdentificationStats.module.scss';

export default function StudentIdentificationStats({
  stats,
  isLoading,
  groupBy,
  isDetailed,
}) {
  const { t } = useTranslation('dashboard\\IdentificationStatsView');
  const { t: publicationsT } = useTranslation('dashboard\\Publications');
  const formatDate = useFormatDate();

  const getCourseTitle = (courseRow) =>
    t(
      courseRow.course === 'all'
        ? 'studentIdentificationAllCoursesTitle'
        : 'studentIdentificationCourseTitle',
    );

  const getCourseHint = (courseRow) =>
    courseRow.course === 'all'
      ? t('studentIdentificationAllCoursesHint')
      : null;

  const renderDetailedStudentList = (statRow) => {
    return (
      <ul className={classes.categoryList}>
        <li>
          <Title type="h4">
            {t('highAbility')}:{' '}
            <span className={classes.categoryFraction}>
              {getFormattedFraction(
                statRow.numStudentsHighAbility,
                statRow.totalStudents,
              )}
            </span>
          </Title>

          <ul>
            {statRow.studentsHighAbility.map((student) => (
              <li key={student.studentId}>
                {student.studentId}
                {student.score !== undefined ? (
                  <>
                    <br />({t('studentScore', { score: student.score })})
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </li>

        <li>
          <Title type="h4">
            {t('lowAbility')}:{' '}
            <span className={classes.categoryFraction}>
              {getFormattedFraction(
                statRow.numStudentsLowAbility,
                statRow.totalStudents,
              )}
            </span>
          </Title>
          <ul>
            {statRow.studentsLowAbility.map((student) => (
              <li key={student.studentId}>
                {student.studentId}
                {student.score !== undefined ? (
                  <>
                    <br />({t('studentScore', { score: student.score })})
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </li>

        <li>
          <Title type="h4">
            {t('significantImprovement')}:{' '}
            <span className={classes.categoryFraction}>
              {getFormattedFraction(
                statRow.numStudentsSignificantImprovement,
                statRow.totalStudents,
              )}
            </span>
          </Title>
          <ul className={classes.studentList}>
            {statRow.studentsSignificantImprovement.map((student) => (
              <li key={student.studentId}>
                {student.studentId}
                <br />(
                {t('improvementObtainedBetweenDates', {
                  date1: formatDate(student.firstEvaluationDate),
                  date2: formatDate(student.lastEvaluationDate),
                  interpolation: { escapeValue: false },
                })}
                {student.improvementObtained !== undefined
                  ? `: ${formatDecimal(student.improvementObtained)}%`
                  : null}
                )
              </li>
            ))}
          </ul>
        </li>
      </ul>
    );
  };

  const renderDetailedStatsSubprocesses = (cognitiveProcessStatRows) => {
    return (
      <ul className={classes.cognitiveSubprocessList}>
        {cognitiveProcessStatRows.map((cognitiveProcessRow) => (
          <li
            key={`${cognitiveProcessRow.cognitiveProcess}-${cognitiveProcessRow.cognitiveSubprocess}`}
          >
            <Title type="h4">
              {publicationsT(cognitiveProcessRow.cognitiveSubprocess)}
            </Title>
            {renderDetailedStudentList(cognitiveProcessRow)}
          </li>
        ))}
      </ul>
    );
  };

  const renderDetailedStats = (courseRow) => {
    const title = t(
      courseRow.course === 'all'
        ? 'studentIdentificationAllCoursesDetailedTitle'
        : 'studentIdentificationCourseDetailedTitle',
    );

    return (
      <>
        <Title type="h3">
          <span style={{ textTransform: 'uppercase' }}>{title}</span>
          {courseRow.course !== 'all' ? `: "${courseRow.course}"` : null}
        </Title>

        <ul className={classes.detailList}>
          {courseRow.data.map((evaluationAgeRow) => {
            const hasDifferentAges = evaluationAgeRow.data.length > 1;

            return (
              <li key={evaluationAgeRow.evaluationAge}>
                <Title type="h4">
                  {t('evaluationAge', {
                    age: getFormattedAge(evaluationAgeRow.evaluationAge),
                  })}
                </Title>

                <ul className={classes.cognitiveProcessList}>
                  {evaluationAgeRow.data.map((cognitiveProcessAgeRow) => {
                    const groupedProcesses = groupByCognitiveProcess(
                      cognitiveProcessAgeRow.data,
                    );

                    return Object.entries(groupedProcesses).map(
                      ([cognitiveProcessName, subprocesses]) => {
                        const processKey = `${evaluationAgeRow.evaluationAge}-${cognitiveProcessAgeRow.cognitiveProcessAge}-${cognitiveProcessName}`;

                        return (
                          <li key={processKey}>
                            <Title type="h4">
                              {publicationsT(cognitiveProcessName)}
                              {hasDifferentAges
                                ? ` (${t('ageYears', {
                                    age: getFormattedAge(
                                      cognitiveProcessAgeRow.cognitiveProcessAge,
                                    ),
                                  })})`
                                : null}
                            </Title>

                            {subprocesses.length > 1
                              ? renderDetailedStatsSubprocesses(subprocesses)
                              : renderDetailedStudentList(subprocesses[0])}
                          </li>
                        );
                      },
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      </>
    );
  };

  const renderCourseStats = ({ courseRow, renderStatsTable }) => {
    const headerColumns = [
      {
        key: 'numStudentsHighAbility',
        content: t('numStudentsHighAbility'),
      },
      {
        key: 'numStudentsLowAbility',
        content: t('numStudentsLowAbility'),
      },
      {
        key: 'numStudentsExpectedImprovement',
        content: t('numStudentsExpectedImprovement'),
      },
      {
        key: 'numStudentsSignificantImprovement',
        content: t('numStudentsSignificantImprovement'),
      },
    ];

    const getBodyColumns = ({ statRow }) => [
      {
        key: 'numStudentsHighAbility',
        content: getFormattedFraction(
          statRow.numStudentsHighAbility,
          statRow.totalStudents,
        ),
      },
      {
        key: 'numStudentsLowAbility',
        content: getFormattedFraction(
          statRow.numStudentsLowAbility,
          statRow.totalStudents,
        ),
      },
      {
        key: 'numStudentsExpectedImprovement',
        content: getFormattedFraction(
          statRow.numStudentsExpectedImprovement,
          statRow.totalStudents,
        ),
      },
      {
        key: 'numStudentsSignificantImprovement',
        content: getFormattedFraction(
          statRow.numStudentsSignificantImprovement,
          statRow.totalStudents,
        ),
      },
    ];

    return (
      <>
        {renderStatsTable({ courseRow, headerColumns, getBodyColumns })}
        {isDetailed ? renderDetailedStats(courseRow) : null}
      </>
    );
  };

  return (
    <IdentificationStatsFactory
      stats={stats}
      isLoading={isLoading}
      groupBy={groupBy}
      getCourseTitle={getCourseTitle}
      getCourseHint={getCourseHint}
      renderCourseStats={renderCourseStats}
    />
  );
}

StudentIdentificationStats.propTypes = {
  stats: PropTypes.object,
  isLoading: PropTypes.bool,
  groupBy: PropTypes.string,
  isDetailed: PropTypes.bool,
};
